import { useEffect, useState } from 'react';
import { onConsentChange } from './on-consent-change';
import type { CheckConsentOptions, UseOneTrustCountryResult } from './types';

/** OneTrust populates its geolocation shortly after the stub script loads. */
const DEFAULT_POLL_INTERVAL_MS = 500;
const DEFAULT_TIMEOUT_MS = 5000;

/**
 * The slice of the OneTrust global this module reads.
 *
 * Declared locally rather than through `declare global`: `Window.OneTrust` is already
 * augmented with incompatible types elsewhere in the monorepo (`unknown` in
 * `@fff-web/fff-global-footer`, a structured type in maui's own OneTrust lib), and a
 * third declaration fails to compile wherever two of them land in the same program.
 */
interface OneTrustGeolocation {
  getGeolocationData?: () => { country?: string } | undefined;
}

/**
 * The geolocated country if OneTrust has loaded and resolved one, else `undefined`.
 * Uppercased so callers can compare against ISO-3166 alpha-2 codes directly.
 */
const readCountry = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;

  try {
    const oneTrust = (window as unknown as { OneTrust?: OneTrustGeolocation }).OneTrust;
    return oneTrust?.getGeolocationData?.()?.country?.toUpperCase() || undefined;
  } catch {
    // Reading the global can throw behind privacy extensions. Treat it as unresolved
    // rather than propagating -- callers gate on the country, they don't handle errors.
    return undefined;
  }
};

/**
 * The country OneTrust geolocated the current request to.
 *
 * Resolved once per mount rather than subscribed to: unlike consent, OneTrust's
 * geolocation is derived from the request IP when the script loads and does not change
 * while the page is open, so there is no equivalent of `OneTrustGroupsUpdated` to watch.
 *
 * `country` is `null` until OneTrust answers, and stays `null` if it never does --
 * blocked, offline, or geolocation disabled on the tenant. A `null` country means
 * "unknown", never "somewhere else": it is deliberately not conflated with a resolved
 * country, so callers decide for themselves whether an unresolved geolocation should
 * open or close their gate. Use `isLoading` to tell "still waiting" from "never
 * answered".
 */
export function useOneTrustCountry(options?: CheckConsentOptions): UseOneTrustCountryResult {
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const [result, setResult] = useState<UseOneTrustCountryResult>(() => {
    const initial = readCountry();
    return { country: initial ?? null, isLoading: initial === undefined };
  });

  useEffect(() => {
    // OneTrust may have landed between the initial render and this effect, in which case
    // there is nothing to poll for -- but `isLoading` still has to be cleared.
    const initial = readCountry();
    if (initial !== undefined) {
      setResult({ country: initial, isLoading: false });
      return;
    }

    const interval = setInterval(() => {
      const resolved = readCountry();
      if (resolved === undefined) return;

      clearInterval(interval);
      clearTimeout(timer);
      setResult({ country: resolved, isLoading: false });
    }, pollIntervalMs);

    // OneTrust never answered in time. Stop polling and leave `country` null so country
    // gates stay shut -- but keep whatever the listener below may already have resolved.
    const timer = setTimeout(() => {
      clearInterval(interval);
      setResult((prev) => (prev.country === null ? { country: null, isLoading: false } : prev));
    }, timeoutMs);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [pollIntervalMs, timeoutMs]);

  // OneTrust can finish loading after the poll above has given up -- a slow network, or a
  // deferred stub. It fires `OneTrustGroupsUpdated` when it initializes, and
  // `useConsentGated` recovers from that same event, so the country has to as well.
  // Without this a late load leaves consent granted but the country stuck at null, which
  // would keep a member blocked until the component remounted.
  useEffect(
    () =>
      onConsentChange(() => {
        const resolved = readCountry();
        if (resolved === undefined) return;

        setResult((prev) =>
          prev.country === resolved && !prev.isLoading
            ? prev
            : { country: resolved, isLoading: false },
        );
      }),
    [],
  );

  return result;
}
